import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, ArrowUp, ArrowDown, Save, Loader2, ImageIcon, Truck, CreditCard } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { apiGetFooter, apiSaveFooter, apiUploadImage, errMsg } from "../adminApi";

const DEFAULT_STYLE = { height: 28, background: "none", monochrome: false, gap: 14 };

function uid() {
  return (crypto?.randomUUID?.() || String(Date.now() + Math.random()));
}

function StylePreview({ logos, style }) {
  const st = { ...DEFAULT_STYLE, ...(style || {}) };
  const chip =
    st.background === "light" ? "bg-white px-3 py-1.5 rounded-md"
    : st.background === "dark" ? "bg-ink border border-line px-3 py-1.5 rounded-md"
    : "";
  return (
    <div className="rounded-lg border border-line bg-ink p-4" data-testid="footer-style-preview">
      <div className="mb-2 text-[10px] uppercase tracking-wider text-tertiary">Anteprima</div>
      {logos.length === 0 ? (
        <p className="text-xs text-steel">Carica un logo per vedere l'anteprima.</p>
      ) : (
        <div className="flex flex-wrap items-center" style={{ gap: `${st.gap}px` }}>
          {logos.map((l) => (
            <div key={l.id} className={`flex items-center justify-center ${chip}`}>
              <img src={l.image_url} alt={l.label || ""} className="w-auto max-w-[120px] object-contain"
                style={{ height: `${st.height}px`, filter: st.monochrome ? "brightness(0) invert(1)" : undefined }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StyleControls({ style, setStyle, testid }) {
  const st = { ...DEFAULT_STYLE, ...(style || {}) };
  const set = (patch) => setStyle({ ...st, ...patch });
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid={testid}>
      <div>
        <Label className="text-xs text-steel">Altezza (px)</Label>
        <Input type="number" min={12} max={96} value={st.height} className="mt-1 h-9 bg-surface2"
          onChange={(e) => set({ height: Math.max(12, Math.min(96, parseInt(e.target.value || "28", 10))) })}
          data-testid="footer-style-height" />
      </div>
      <div>
        <Label className="text-xs text-steel">Spaziatura (px)</Label>
        <Input type="number" min={4} max={48} value={st.gap} className="mt-1 h-9 bg-surface2"
          onChange={(e) => set({ gap: Math.max(4, Math.min(48, parseInt(e.target.value || "14", 10))) })}
          data-testid="footer-style-gap" />
      </div>
      <div>
        <Label className="text-xs text-steel">Sfondo</Label>
        <Select value={st.background} onValueChange={(v) => set({ background: v })}>
          <SelectTrigger className="mt-1 h-9 bg-surface2" data-testid="footer-style-bg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nessuno (trasparente)</SelectItem>
            <SelectItem value="light">Chiaro</SelectItem>
            <SelectItem value="dark">Scuro</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col">
        <Label className="text-xs text-steel">Monocromatico (bianco)</Label>
        <div className="mt-2 flex h-9 items-center">
          <Switch checked={st.monochrome} onCheckedChange={(v) => set({ monochrome: v })} data-testid="footer-style-mono" />
        </div>
      </div>
    </div>
  );
}

function LogoRow({ logo, onLabel, onRemove, onUp, onDown, isFirst, isLast }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-line bg-surface3 p-3" data-testid="footer-logo-row">
      <div className="flex h-12 w-20 shrink-0 items-center justify-center rounded-md bg-ink px-2">
        <img src={logo.image_url} alt={logo.label || "logo"} className="h-8 w-auto max-w-[72px] object-contain" />
      </div>
      <Input
        value={logo.label}
        onChange={(e) => onLabel(e.target.value)}
        placeholder="Etichetta (es. DHL, Visa)"
        className="h-9 bg-surface2"
        data-testid="footer-logo-label-input"
      />
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onUp} disabled={isFirst} aria-label="Su">
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDown} disabled={isLast} aria-label="Giù">
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onRemove} aria-label="Rimuovi" data-testid="footer-logo-remove">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function LogoListEditor({ title, icon: Icon, items, setItems, style, setStyle, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/")) {
          toast.error(`"${f.name}" non è un'immagine`);
          continue;
        }
        const res = await apiUploadImage(f, "rinova/footer");
        const url = res.url_normalized || res.url;
        setItems((prev) => [...prev, { id: uid(), label: "", image_url: url }]);
      }
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const update = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) => setItems((prev) => {
    const arr = [...prev];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  return (
    <Card className="border-line bg-surface2" data-testid={testid}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-display text-base">
          <Icon className="h-4 w-4 text-tertiary" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {items.length === 0 ? (
          <div className="flex flex-col items-start gap-1 rounded-lg border border-dashed border-line p-5 text-sm text-steel">
            <ImageIcon className="h-5 w-5 text-tertiary" />
            <p className="mt-1">Nessun logo. Carica i PNG (meglio se con sfondo trasparente).</p>
          </div>
        ) : (
          items.map((logo, i) => (
            <LogoRow
              key={logo.id || i}
              logo={logo}
              onLabel={(v) => update(i, { label: v })}
              onRemove={() => remove(i)}
              onUp={() => move(i, -1)}
              onDown={() => move(i, 1)}
              isFirst={i === 0}
              isLast={i === items.length - 1}
            />
          ))
        )}
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onPick} data-testid={`${testid}-file-input`} />
        <Button variant="secondary" size="sm" className="self-start" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload-button`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Carica logo PNG
        </Button>

        <div className="mt-1 border-t border-line pt-4">
          <StyleControls style={style} setStyle={setStyle} testid={`${testid}-style`} />
          <div className="mt-4">
            <StylePreview logos={items} style={style} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FooterSettings() {
  const [shipping, setShipping] = useState([]);
  const [payments, setPayments] = useState([]);
  const [shippingStyle, setShippingStyle] = useState(DEFAULT_STYLE);
  const [paymentStyle, setPaymentStyle] = useState(DEFAULT_STYLE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiGetFooter()
      .then((d) => {
        setShipping(d.shipping_partners || []);
        setPayments(d.payment_methods || []);
        setShippingStyle({ ...DEFAULT_STYLE, ...(d.shipping_style || {}) });
        setPaymentStyle({ ...DEFAULT_STYLE, ...(d.payment_style || {}) });
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = (arr) => arr.map((it) => ({ id: it.id, label: it.label || "", image_url: it.image_url }));
      const res = await apiSaveFooter({
        shipping_partners: clean(shipping),
        payment_methods: clean(payments),
        shipping_style: shippingStyle,
        payment_style: paymentStyle,
      });
      setShipping(res.shipping_partners || []);
      setPayments(res.payment_methods || []);
      if (res.shipping_style) setShippingStyle({ ...DEFAULT_STYLE, ...res.shipping_style });
      if (res.payment_style) setPaymentStyle({ ...DEFAULT_STYLE, ...res.payment_style });
      toast.success("Footer salvato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Footer</h1>
          <p className="mt-1 text-sm text-steel">Loghi dei partner di spedizione e dei metodi di pagamento, con controllo di dimensione, sfondo e colore.</p>
        </div>
        <Button variant="lime" size="sm" onClick={save} disabled={saving || loading} data-testid="footer-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salva
        </Button>
      </div>

      {loading ? (
        <>
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </>
      ) : (
        <>
          <LogoListEditor title="Partner di spedizione" icon={Truck} items={shipping} setItems={setShipping} style={shippingStyle} setStyle={setShippingStyle} testid="footer-shipping-editor" />
          <LogoListEditor title="Metodi di pagamento" icon={CreditCard} items={payments} setItems={setPayments} style={paymentStyle} setStyle={setPaymentStyle} testid="footer-payments-editor" />
          <p className="text-xs text-steel">Suggerimento: usa PNG con sfondo trasparente. Con "Monocromatico" i loghi diventano bianchi (ideale per loghi scuri). Le modifiche vanno online con la pubblicazione dei contenuti.</p>
        </>
      )}
    </div>
  );
}
